import { useState } from 'react';
import { Upload, FileJson, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';

export default function ImportModal({ open, onClose, onImport }) {
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  const handleImport = () => {
    try {
      const data = JSON.parse(jsonText);
      
      // Validate structure
      if (!data.characters && !Array.isArray(data)) {
        // If it's a single character, wrap it
        if (data.name) {
          onImport({ characters: [data] });
        } else {
          throw new Error('Invalid JSON structure. Expected { characters: [...] } or a single character object.');
        }
      } else if (Array.isArray(data)) {
        onImport({ characters: data });
      } else {
        onImport(data);
      }
      
      setJsonText('');
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setJsonText(e.target.result);
      setError('');
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/json') {
      handleFileUpload(file);
    } else {
      setError('Please drop a valid JSON file');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-stone-900 border-stone-700 max-w-2xl" data-testid="import-modal">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-amber-500 flex items-center gap-2">
            <FileJson className="w-5 h-5" />
            Import Characters from JSON
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragOver 
                ? 'border-amber-500 bg-amber-500/10' 
                : 'border-stone-700 hover:border-stone-600'
            }`}
          >
            <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragOver ? 'text-amber-500' : 'text-stone-500'}`} />
            <p className="text-stone-400 mb-2">Drag & drop a JSON file here</p>
            <p className="text-stone-500 text-sm mb-4">or</p>
            <label className="inline-block">
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
                className="hidden"
                data-testid="json-file-input"
              />
              <Button variant="outline" className="border-stone-700 text-stone-300 hover:text-amber-500 cursor-pointer">
                Browse Files
              </Button>
            </label>
          </div>

          {/* JSON textarea */}
          <div>
            <p className="text-stone-400 text-sm mb-2">Or paste JSON directly:</p>
            <Textarea
              value={jsonText}
              onChange={(e) => { setJsonText(e.target.value); setError(''); }}
              placeholder='{"characters": [{ "name": "...", "type": "boss", ... }]}'
              className="bg-stone-800 border-stone-700 text-stone-200 font-mono text-sm min-h-[200px]"
              data-testid="json-textarea"
            />
          </div>

          {/* Error message */}
          {error && (
            <Alert variant="destructive" className="bg-red-950/50 border-red-900">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Example format */}
          <details className="text-sm">
            <summary className="text-stone-500 cursor-pointer hover:text-stone-400">
              View example JSON format
            </summary>
            <pre className="mt-2 p-3 bg-stone-950 rounded text-xs text-stone-400 overflow-auto max-h-48">
{`{
  "characters": [
    {
      "name": "The Jackal",
      "type": "boss",
      "alignment": "enemy",
      "description": "A desert raider leader...",
      "stats": {
        "hp": { "current": 120, "max": 120 },
        "ac": 15,
        "speed": 30
      },
      "actions": [
        {
          "name": "Twin Revolver",
          "category": "attack",
          "damage": { "dice": "1d10", "type": "piercing" }
        }
      ]
    }
  ]
}`}
            </pre>
          </details>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-stone-800">
            <Button variant="outline" onClick={onClose} className="border-stone-700 text-stone-300">
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!jsonText.trim()}
              className="bg-amber-700 hover:bg-amber-600 text-stone-100"
              data-testid="import-submit-btn"
            >
              Import Characters
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
